import React from 'react';
import {
  Alert, Button, ErrorState, NumberInput, SkeletonText, TextInput, useToast,
} from '@parsina/framework';
import { Pencil, Plus } from 'lucide-react';

import { useTranslation, toAsciiDigits } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import {
  CATEGORIES_QUERY, SETTINGS_QUERY, SET_COMMISSION, UPSERT_CATEGORY,
} from './queries';

/** A commission is a percentage the operator types: 0–100, up to two decimals. */
export function parseCommission(raw) {
  // The Arabic/Persian decimal separator (U+066B), the Arabic comma (U+060C) and
  // the ASCII comma (the French decimal mark, «7,5») all mean a decimal point
  // here: a percentage is never grouped, so a comma can only be a decimal.
  const text = toAsciiDigits(String(raw ?? '')).replace(/[\u066B\u060C,]/g, '.').trim();
  if (!text || !/^\d{1,3}(\.\d{1,2})?$/.test(text)) return null;
  const n = Number(text);
  return n >= 0 && n <= 100 ? n : null;
}

function Card({ title, children, 'data-testid': testId }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid={testId}>
      <h2 className="mb-3 text-sm font-bold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

/**
 * The rail: the platform commission and the marketplace taxonomy. Two
 * independent regions — one failing never blanks the other (§2.4).
 */
export default function SettingsCard() {
  const { t } = useTranslation();
  return (
    <aside className="flex flex-col gap-4" aria-label={t('marketplace.settings.title')} data-testid="mk-settings">
      <CommissionCard />
      <CategoriesCard />
    </aside>
  );
}

function CommissionCard() {
  const { t, formatDate } = useTranslation();
  const toast = useToast();
  const [settings, setSettings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [value, setValue] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(SETTINGS_QUERY)
      .then((d) => {
        const s = d?.platformMarketplaceSettings || null;
        setSettings(s);
        // Prefilled in the form NumberInput itself stores — ASCII digits, «.» as
        // the decimal point — never a locale-formatted number: French formats
        // 7.5 as «7,5», which the field's own edit path would not read back.
        setValue(s ? String(s.commissionPct) : '');
      })
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [t]);

  React.useEffect(() => { load(); }, [load]);

  const parsed = parseCommission(value);
  const dirty = settings && parsed !== settings.commissionPct;
  const invalid = value !== '' && parsed == null;

  const save = async () => {
    if (parsed == null || pending) return;
    setPending(true);
    setSaveError(null);
    try {
      const d = await gql(SET_COMMISSION, { pct: parsed });
      setSettings(d?.setPlatformCommission || settings);
      toast.success(t('marketplace.settings.commission.saved'));
    } catch (e) {
      setSaveError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  return (
    <Card title={t('marketplace.settings.commission.title')} data-testid="mk-commission">
      {loading ? (
        <div role="status" aria-busy="true" aria-label={t('marketplace.settings.commission.loading')} data-testid="mk-commission-loading">
          <SkeletonText width="60%" height={14} />
          <div className="mt-3"><SkeletonText width="100%" height={36} /></div>
        </div>
      ) : error ? (
        <ErrorState
          title={t('marketplace.settings.commission.error')}
          description={error}
          onRetry={load}
          retryLabel={t('common.actions.retry')}
          data-testid="mk-commission-error"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {saveError ? <Alert variant="error" data-testid="mk-commission-save-error">{saveError}</Alert> : null}
          <div data-testid="mk-commission-input">
            <NumberInput
              label={t('marketplace.settings.commission.label')}
              value={value}
              onChange={(e) => setValue(e.target?.value ?? e)}
              unit="%"
              helperText={t('marketplace.settings.commission.help')}
              error={invalid ? t('marketplace.settings.commission.invalid') : undefined}
              disabled={pending}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {settings?.updatedAt ? t('marketplace.settings.commission.updatedAt', { date: formatDate(settings.updatedAt) }) : null}
            </span>
            <Button size="sm" onClick={save} disabled={!dirty || invalid || pending} aria-busy={pending || undefined} data-testid="mk-commission-save">
              {t('marketplace.settings.commission.save')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function CategoriesCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [editing, setEditing] = React.useState(null);   // { id, name }
  const [pending, setPending] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(CATEGORIES_QUERY)
      .then((d) => setRows(d?.marketplaceCategories || []))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [t]);

  React.useEffect(() => { load(); }, [load]);

  const upsert = async (input, successKey, after) => {
    setPending(true);
    setSaveError(null);
    try {
      const d = await gql(UPSERT_CATEGORY, { input });
      const saved = d?.upsertMarketplaceCategory;
      if (saved) {
        setRows((r) => (r.some((c) => c.id === saved.id) ? r.map((c) => (c.id === saved.id ? saved : c)) : [...r, saved]));
      }
      toast.success(t(successKey));
      after();
    } catch (e) {
      setSaveError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const add = () => {
    const name = draft.trim();
    if (!name) return;
    const sortOrder = rows.reduce((m, c) => Math.max(m, c.sortOrder), 0) + 1;
    upsert({ name, sortOrder }, 'marketplace.settings.categories.added', () => setDraft(''));
  };

  const rename = () => {
    const name = editing?.name.trim();
    const original = rows.find((c) => c.id === editing?.id);
    if (!name || !original) return;
    upsert({ id: original.id, name, parentId: original.parentId, sortOrder: original.sortOrder },
      'marketplace.settings.categories.renamed', () => setEditing(null));
  };

  return (
    <Card title={t('marketplace.settings.categories.title')} data-testid="mk-categories">
      {loading ? (
        <div role="status" aria-busy="true" aria-label={t('marketplace.settings.categories.loading')} data-testid="mk-categories-loading">
          {[0, 1, 2].map((i) => <div key={i} className="mb-2"><SkeletonText width={`${80 - i * 15}%`} height={14} /></div>)}
        </div>
      ) : error ? (
        <ErrorState
          title={t('marketplace.settings.categories.error')}
          description={error}
          onRetry={load}
          retryLabel={t('common.actions.retry')}
          data-testid="mk-categories-error"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {saveError ? <Alert variant="error" data-testid="mk-categories-save-error">{saveError}</Alert> : null}
          {rows.length ? (
            <ul aria-label={t('marketplace.settings.categories.aria')} className="flex flex-col divide-y divide-border" data-testid="mk-categories-list">
              {rows.map((c) => (
                <li key={c.id} className="flex items-center gap-2 py-1.5" data-testid={`mk-category-${c.id}`}>
                  {editing?.id === c.id ? (
                    <>
                      <div className="min-w-0 flex-1">
                        <TextInput
                          label={t('marketplace.settings.categories.nameLabel')}
                          value={editing.name}
                          onChange={(e) => setEditing({ ...editing, name: e.target?.value ?? e })}
                          maxLength={80}
                          disabled={pending}
                        />
                      </div>
                      <Button size="sm" onClick={rename} disabled={pending || !editing.name.trim() || editing.name.trim() === c.name} data-testid={`mk-category-${c.id}-save`}>
                        {t('marketplace.settings.categories.save')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={pending}>
                        {t('marketplace.settings.categories.cancel')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <span dir="auto" className="min-w-0 flex-1 truncate text-sm text-foreground">{c.name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSaveError(null); setEditing({ id: c.id, name: c.name }); }}
                        aria-label={t('marketplace.settings.categories.rename', { name: c.name })}
                        data-testid={`mk-category-${c.id}-rename`}
                      >
                        <Pencil size={14} aria-hidden />
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="mk-categories-empty">{t('marketplace.settings.categories.empty')}</p>
          )}
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1" data-testid="mk-categories-new">
              <TextInput
                label={t('marketplace.settings.categories.nameLabel')}
                placeholder={t('marketplace.settings.categories.newPlaceholder')}
                value={draft}
                onChange={(e) => setDraft(e.target?.value ?? e)}
                maxLength={80}
                disabled={pending}
              />
            </div>
            <Button size="sm" variant="outline" onClick={add} disabled={pending || !draft.trim()} data-testid="mk-categories-add">
              <Plus size={14} aria-hidden />
              {t('marketplace.settings.categories.add')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
